import { useEffect, useRef, useState } from "react";
import { startReportGeneration } from "./report-generation";
import { Loader2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { type LessonReportRow } from "./types";

export function LessonGenerateDialog({
  lessonId,
  startDate,
  endDate,
  onClose,
  onDone,
}: {
  lessonId: number;
  startDate: string;
  endDate: string;
  onClose: () => void;
  onDone: (r: LessonReportRow) => void;
}) {
  const [generating, setGenerating] = useState(false);
  const [polling, setPolling] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const notFoundCountRef = useRef(0);

  const start = async () => {
    setGenerating(true);
    setError(null);
    notFoundCountRef.current = 0;
    try {
      await startReportGeneration("lesson", { lessonId, startDate, endDate });
      setPolling(true);
    } catch (cause) {
      setError(cause instanceof Error ? cause.message : "Failed to start generation.");
      setGenerating(false);
    }
  };

  useEffect(() => {
    if (!polling) return;
    const interval = setInterval(async () => {
      try {
        const params = new URLSearchParams({
          lessonId: String(lessonId),
          startDate,
          endDate,
        });
        const r = await fetch(
          `/api/internal/admin/lesson-reports/lookup?${params}`,
        );
        if (r.status === 404) {
          notFoundCountRef.current += 1;
          if (notFoundCountRef.current >= 6) {
            clearInterval(interval);
            setError(
              "Report generation failed — Lambda may have crashed. Check CloudWatch logs.",
            );
            setPolling(false);
            setGenerating(false);
          }
          return;
        }
        if (!r.ok) return;
        notFoundCountRef.current = 0;
        const data = (await r.json()) as LessonReportRow;
        if (data.status === "generated") {
          clearInterval(interval);
          onDone(data);
        } else if (data.status === "stale") {
          clearInterval(interval);
          setError("Regeneration failed — showing previous report.");
          setPolling(false);
          setGenerating(false);
          onDone(data);
        } else if (data.status === "failed") {
          clearInterval(interval);
          setError("Report generation failed.");
          setPolling(false);
          setGenerating(false);
        } else if (data.status === "no_data") {
          clearInterval(interval);
          setError("No sessions found for this date range.");
          setPolling(false);
          setGenerating(false);
        }
      } catch {
        /* keep polling */
      }
    }, 5000);
    return () => clearInterval(interval);
  }, [polling, lessonId, startDate, endDate, onDone]);

  return (
    <Dialog
      open
      onOpenChange={(open) => {
        if (!open && !generating) onClose();
      }}
    >
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Generate Lesson Report</DialogTitle>
        </DialogHeader>
        <div className="flex flex-col gap-4 py-2">
          <div className="grid grid-cols-3 gap-3 text-sm">
            <div>
              <p className="text-xs text-muted-foreground mb-1">Lesson ID</p>
              <p className="font-medium">{lessonId}</p>
            </div>
            <div>
              <p className="text-xs text-muted-foreground mb-1">Start Date</p>
              <p className="font-medium">{startDate}</p>
            </div>
            <div>
              <p className="text-xs text-muted-foreground mb-1">End Date</p>
              <p className="font-medium">{endDate}</p>
            </div>
          </div>
          {polling && (
            <div className="flex items-center gap-2 text-sm text-muted-foreground">
              <Loader2 className="size-4 animate-spin" />
              Running… this may take a few minutes
            </div>
          )}
          {error && <p className="text-sm text-destructive">{error}</p>}
        </div>
        <DialogFooter>
          <Button
            variant="outline"
            onClick={onClose}
            disabled={generating && polling}
          >
            Cancel
          </Button>
          <Button onClick={start} disabled={generating}>
            {generating ? (
              <>
                <Loader2 className="size-4 animate-spin mr-2" />
                Generating…
              </>
            ) : (
              "Generate"
            )}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
